import React, { Component } from "react";
import type { ApolloClient } from "@apollo/client";
import { connect, type ConnectedProps } from "react-redux";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import StepWizard from "components/StepWizard";
import StepInfo from "components/StepInfo";
import { clearUploadedDocuments } from "actions/userActions";
import { adminUpdateUserProfile } from "actions/adminActions";
import UserProfileForm from "pages/dashboards/individualDashboard/profile/UserProfileForm";
import UploadDocumentation from "pages/dashboards/individualDashboard/profile/UploadDocumentation";
import { filter, isEqual, map, pick, toLower } from "lodash";
import { ID_DOCUMENT } from "statics/docTypes";
import { createLoadingSelector } from "store/selectors";
import { adminConstants } from "actions/types";
import { toast } from "react-toastify";
import type { RootState } from "types/state";
import type { DocumentInput, UserProfileInput } from "types/graphql";

/**
 * The userInfo profile fields seeded into the form and edited in local state.
 * All optional — seeded from `props.userInfo` (any subset) and normalized to
 * "" so the inputs stay controlled.
 */
type ProfileInfo = Partial<Record<(typeof PROFILE_FIELDS)[number], string>>;

/**
 * The runtime shape of an uploaded document as stored in
 * `state.userDocuments.documents` by `persistDocument` (actions/userActions).
 * The `Document` model on `UserDocumentsState.documents` exposes `uri`/`type`,
 * but the objects placed in state actually carry `docUrl`/`docType`/`subDocType`
 * — type the boundary read to the real runtime keys.
 */
interface UploadedDocument {
  docUrl?: string;
  docType?: string;
  subDocType?: string;
  contentType?: string;
  name?: string;
}

const PROFILE_FIELDS = [
  "legalName",
  "firstName",
  "lastName",
  "address1",
  "address2",
  "city",
  "state",
  "postalCode",
  "phoneNumber",
  "email",
] as const;

const stepToLabels: Record<string, { subheader: string }> = {
  1: {
    subheader: "Update the user's personal information.",
  },
  2: {
    subheader: "Upload verifying documentation.",
  },
};

const steps = [
  {
    index: 1,
    label: "Update Info",
  },
  {
    index: 2,
    label: "Upload Documentation",
  },
];

const loadingSelector = createLoadingSelector([
  adminConstants.ADMIN_UPDATE_USER_PROFILE,
]);

const mapStateToProps = (state: RootState) => ({
  isFetching: loadingSelector(state),
  // The Document model on userDocuments.documents exposes `uri`/`type`; the
  // objects stored at runtime carry `docType`/`docUrl`/`subDocType` (see
  // UploadedDocument) — cast at this boundary so the `docType` predicate and
  // later `docUrl`/`subDocType` reads typecheck against the real runtime shape.
  documents: filter(
    state.userDocuments.documents as unknown as UploadedDocument[],
    { docType: ID_DOCUMENT }
  ),
});

const mapDispatchToProps = {
  adminUpdateUserProfile,
  clearUploadedDocuments,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

interface OwnProps {
  userId?: string;
  /** The already-loaded profile used to seed the form and diff changes. */
  userInfo: ProfileInfo;
  onSuccess?: () => void;
  onCancel?: () => void;
}

type Props = WithApolloClient<OwnProps & PropsFromRedux>;

interface State {
  // string on init ("1"); StepWizard's onStepChange feeds back a number
  activeStep: string | number;
  userInfo: ProfileInfo;
}

class AdminUserProfileWorkflow extends Component<Props, State> {
  constructor(props: Props) {
    super(props);

    // Seed the form from the profile the admin already loaded, normalizing
    // any nullable fields to "" so the inputs stay controlled.
    const seeded = PROFILE_FIELDS.reduce<ProfileInfo>((acc, field) => {
      acc[field] = props.userInfo?.[field] ?? "";
      return acc;
    }, {});

    this.state = {
      activeStep: "1",
      userInfo: seeded,
    };
  }

  componentDidMount() {
    // Clear any documents lingering in shared state from a prior edit so they
    // are not attached to this user.
    this.props.clearUploadedDocuments();
  }

  componentWillUnmount() {
    this.props.clearUploadedDocuments();
  }

  submitUserProfile = () => {
    const payload: UserProfileInput = pick(this.state.userInfo, PROFILE_FIELDS);

    payload.documents = map(this.props.documents, (doc): DocumentInput => {
      return {
        type: ID_DOCUMENT,
        // doc.docUrl/name/contentType are optional on UploadedDocument but the
        // DocumentInput fields are required strings — coerce, preserving the
        // original runtime behavior (undefined flows through unchanged).
        url: doc.docUrl as string,
        contentType: doc.contentType as string,
        name: doc.name as string,
        metadata: { subType: doc.subDocType },
      };
    });

    void this.props
      .adminUpdateUserProfile(
        this.props.client as ApolloClient<object>,
        // userId is optional on OwnProps; action creator requires string|number
        this.props.userId as string,
        payload
      )
      .then((result) => {
        if (result.success) {
          toast.success("Profile updated successfully");
          // optional callback invoked unconditionally — preserve throw-on-undefined
          this.props.onSuccess!();
        } else {
          toast.error("There was an error updating this user's profile.");
        }
      });
  };

  onStepChange = ({ activeStep }: { activeStep: number }) => {
    this.setState({ activeStep });
  };

  updateUserInfo = (userInfo: ProfileInfo) => {
    this.setState({
      userInfo: {
        ...this.state.userInfo,
        ...userInfo,
      },
    });
  };

  checkIfNameChanged = () => {
    const { firstName, lastName } = this.state.userInfo;
    return (
      firstName !== this.props.userInfo.firstName ||
      lastName !== this.props.userInfo.lastName
    );
  };

  checkIfPhoneChanged = () => {
    return this.state.userInfo.phoneNumber !== this.props.userInfo.phoneNumber;
  };

  checkIfAddressChanged = () => {
    const addressFields = [
      "address1",
      "address2",
      "state",
      "postalCode",
      "city",
    ] as const;
    const toLowered = (source: ProfileInfo) =>
      addressFields.reduce<Record<string, string>>((acc, field) => {
        acc[field] = toLower(source[field]);
        return acc;
      }, {});

    return !isEqual(
      toLowered(this.state.userInfo),
      toLowered(this.props.userInfo)
    );
  };

  render() {
    const activeSectionLabels = stepToLabels[this.state.activeStep];

    const nameWasChanged = this.checkIfNameChanged();
    const addressWasChanged = this.checkIfAddressChanged();
    const phoneWasChanged = this.checkIfPhoneChanged();

    return (
      <>
        <p className="steps-header">{activeSectionLabels.subheader}</p>
        <StepInfo steps={steps} activeStepNumber={this.state.activeStep} />
        <StepWizard
          onStepChange={this.onStepChange}
          isLazyMount={true}
          transitions={{
            enterRight: "",
            enterLeft: "",
            exitRight: "",
            exitLeft: "",
          }}
        >
          <UserProfileForm
            userInfo={this.state.userInfo}
            updateUserInfo={this.updateUserInfo}
            stepName={"updateInfo"}
            // onCancel is optional on this workflow's OwnProps but required by
            // UserProfileForm; forwarded verbatim (undefined flows through
            // unchanged at runtime, preserving prior behavior).
            // @ts-expect-error optional own prop forwarded to a required target prop
            onCancel={this.props.onCancel}
          />
          <UploadDocumentation
            userInfo={this.state.userInfo}
            stepName={"uploadDocuments"}
            addressWasChanged={addressWasChanged}
            nameWasChanged={nameWasChanged}
            phoneWasChanged={phoneWasChanged}
            onSubmit={this.submitUserProfile}
            documents={this.props.documents}
            isUpdating={this.props.isFetching}
          />
        </StepWizard>
      </>
    );
  }
}

export default connector(withApollo<Props>(AdminUserProfileWorkflow));
